import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Eye, EyeOff } from "lucide-react";

const LoginPage = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { login, logout } = useAuth();

  const [formData, setFormData]       = useState({ email: "", password: "" });
  const [loading, setLoading]         = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Read redirect intent from URL — e.g. /login?redirect=ai-filing
  const params       = new URLSearchParams(location.search);
  const redirectTo   = params.get("redirect");
  const isAIRedirect = redirectTo === "ai-filing";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(formData.email, formData.password);

      if (user?.user_type === "admin") {
        toast.error("Please use the admin login page");
        logout();
        navigate("/login");
        return;
      }

      toast.success("Welcome back!");

      // Phase 2 — if user came via AI Filing CTA, redirect there
      if (isAIRedirect) {
        navigate("/ai-filing", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-12"
      style={{ backgroundColor: "var(--secondary)" }}
    >
      <div
        className="w-full max-w-5xl grid md:grid-cols-2 overflow-hidden rounded-2xl border bg-white shadow-sm"
        style={{ borderColor: "var(--border)" }}
      >
        {/* Left: Form */}
        <div className="p-10 md:p-12 flex flex-col justify-center">
          <div className="mb-8">
            <div
              className="inline-flex items-center rounded-full px-3 py-1 text-sm mb-4"
              style={{ backgroundColor: "var(--secondary)" }}
            >
              {isAIRedirect ? "Sign in to start AI Filing" : "Secure Client Access"}
            </div>
            <h1
              className="text-4xl font-bold leading-tight mb-3"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Welcome back to MoolTax
            </h1>
            <p className="text-base opacity-75 leading-7">
              {isAIRedirect
                ? "Sign in and you will be taken directly to AI Tax Filing."
                : "Sign in to view your tax filing requests, upload documents, and stay connected with your assigned team."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium mb-2">
                Email address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2"
                style={{ borderColor: "var(--border)" }}
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  autoComplete="current-password"
                  className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 pr-12"
                  style={{ borderColor: "var(--border)" }}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-80"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3 rounded-lg disabled:opacity-50"
            >
              {loading ? "Signing in..." : isAIRedirect ? "Sign In & Start AI Filing" : "Sign In"}
            </button>
          </form>

          <p className="text-sm text-center mb-2">
            <Link
              to="/forgot-password"
              className="text-sm opacity-60 hover:opacity-100 underline"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </Link>
          </p>

          <p className="text-sm opacity-65">
            New here?{" "}
            <Link
              to={isAIRedirect ? "/register?redirect=ai-filing" : "/register"}
              className="underline"
            >
              Create your account
            </Link>
          </p>
        </div>

        {/* Right: Info panel */}
        <div
          className="hidden md:flex flex-col justify-center p-12"
          style={{
            background: "linear-gradient(135deg, var(--secondary) 0%, #ffffff 100%)",
          }}
        >
          <div
            className="rounded-2xl border bg-white p-8"
            style={{ borderColor: "var(--border)" }}
          >
            {isAIRedirect ? (
              // AI Filing context panel
              <>
                <h2
                  className="text-2xl font-semibold mb-4"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  AI Tax Filing — what to expect
                </h2>
                <p className="opacity-75 leading-7 mb-6">
                  After signing in you will land directly on the AI Filing page.
                  Upload your documents and our AI handles the rest.
                </p>
                <div className="space-y-4">
                  {[
                    { title: "Upload documents",   desc: "Form 16, 26AS, bank statements & more" },
                    { title: "AI analyses",         desc: "Reads every document, computes tax" },
                    { title: "Best regime selected", desc: "Old vs New — AI picks lower tax" },
                    { title: "Download ITR JSON",   desc: "File directly on incometax.gov.in" },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="rounded-xl p-4"
                      style={{ backgroundColor: "var(--secondary)" }}
                    >
                      <div className="font-medium mb-1">{item.title}</div>
                      <div className="text-sm opacity-75">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              // Default context panel
              <>
                <h2
                  className="text-2xl font-semibold mb-4"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  Filing made clearer
                </h2>
                <p className="opacity-75 leading-7 mb-6">
                  MoolTax helps you move from document collection to review and
                  filing with less confusion and better visibility at every step.
                </p>
                <div className="space-y-4">
                  {[
                    { title: "Track case progress",    desc: "See request status and updates in one place." },
                    { title: "Upload with confidence", desc: "Submit documents securely and communicate easily." },
                    { title: "Built for trust",        desc: "Simple flow, careful review, and secure access." },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="rounded-xl p-4"
                      style={{ backgroundColor: "var(--secondary)" }}
                    >
                      <div className="font-medium mb-1">{item.title}</div>
                      <div className="text-sm opacity-75">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;